import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Megaphone, Plus, Trash2, Edit3, X, Check } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

/**
 * AdminAnnouncementsPage — Angela Feb 2026 v2
 * Simplified per PRD: single `message` field + `active` toggle.
 * Admin controls when a message stops appearing by deactivating it —
 * no automatic expiration.
 */
const AdminAnnouncementsPage = ({ Layout }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [message, setMessage] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/announcements`);
      setItems(r.data.items || []);
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to load announcements");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditing({}); setMessage(""); setError(""); };
  const openEdit = (a) => { setEditing(a); setMessage(a.message || ""); setError(""); };
  const close = () => { setEditing(null); setMessage(""); setError(""); };

  const save = async () => {
    setError("");
    if (!message.trim()) { setError("The message cannot be empty."); return; }
    setSaving(true);
    try {
      if (editing && editing.id) {
        await axios.patch(`${API}/admin/announcements/${editing.id}`, { message });
      } else {
        await axios.post(`${API}/admin/announcements`, { message });
      }
      close(); load();
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to save");
    } finally { setSaving(false); }
  };

  const remove = async (a) => {
    if (!window.confirm("Delete this announcement?")) return;
    try { await axios.delete(`${API}/admin/announcements/${a.id}`); load(); }
    catch (e) { alert(e.response?.data?.detail || "Failed to delete"); }
  };

  const toggleActive = async (a) => {
    try { await axios.patch(`${API}/admin/announcements/${a.id}`, { active: !a.active }); load(); }
    catch (e) { alert(e.response?.data?.detail || "Failed to update"); }
  };

  return (
    <Layout>
      <div className="max-w-4xl mx-auto" data-testid="admin-announcements-page">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-[#D1A054]/15">
              <Megaphone className="h-5 w-5 text-[#D1A054]" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-[#1F2937]">Inspector Announcements</h1>
              <p className="text-xs text-gray-500">Messages that block My Orders until every inspector confirms.</p>
            </div>
          </div>
          <button
            onClick={openNew}
            className="flex items-center gap-2 px-4 py-2 bg-[#2F5D8A] hover:bg-[#3B6D9C] text-white text-sm font-semibold rounded-lg transition-colors"
            data-testid="announcement-new-btn"
          >
            <Plus className="h-4 w-4" /> New announcement
          </button>
        </div>

        {loading ? (
          <div className="text-center py-8 text-gray-400 text-sm">Loading…</div>
        ) : items.length === 0 ? (
          <div className="text-center py-12 bg-white border border-gray-200 rounded-lg" data-testid="announcements-empty">
            <Megaphone className="h-10 w-10 text-gray-300 mx-auto mb-2" />
            <p className="text-sm text-gray-500">No announcements yet.</p>
            <p className="text-xs text-gray-400 mt-1">Click &quot;New announcement&quot; to create one.</p>
          </div>
        ) : (
          <div className="bg-white border border-gray-200 rounded-lg divide-y divide-gray-100" data-testid="announcements-list">
            {items.map((a) => (
              <div key={a.id} className="p-4 flex items-start gap-4" data-testid={`announcement-row-${a.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    {a.active
                      ? <span className="text-[10px] px-2 py-0.5 rounded bg-emerald-100 text-emerald-700 font-semibold">ACTIVE</span>
                      : <span className="text-[10px] px-2 py-0.5 rounded bg-gray-100 text-gray-500 font-semibold">INACTIVE</span>}
                    <span className="text-[11px] text-gray-400">by {a.created_by}</span>
                  </div>
                  <p className="text-sm text-gray-800 whitespace-pre-wrap leading-relaxed">{a.message}</p>
                </div>
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  <button
                    onClick={() => toggleActive(a)}
                    className={`px-2.5 py-1 text-[11px] font-semibold rounded ${a.active ? "bg-gray-100 hover:bg-gray-200 text-gray-700" : "bg-emerald-100 hover:bg-emerald-200 text-emerald-700"}`}
                    data-testid={`announcement-toggle-${a.id}`}
                  >
                    {a.active ? "Deactivate" : "Activate"}
                  </button>
                  <button onClick={() => openEdit(a)} className="p-1.5 text-gray-500 hover:text-[#2F5D8A]" data-testid={`announcement-edit-${a.id}`}>
                    <Edit3 className="h-4 w-4" />
                  </button>
                  <button onClick={() => remove(a)} className="p-1.5 text-gray-500 hover:text-red-600" data-testid={`announcement-delete-${a.id}`}>
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {editing !== null && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" data-testid="announcement-editor">
            <div className="bg-white rounded-lg max-w-lg w-full mx-4 shadow-2xl">
              <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200">
                <h3 className="text-sm font-bold text-[#1F2937]">{editing.id ? "Edit announcement" : "New announcement"}</h3>
                <button onClick={close} className="p-1 text-gray-500 hover:text-gray-700" data-testid="announcement-editor-close"><X className="h-4 w-4" /></button>
              </div>
              <div className="p-5 space-y-4">
                <div>
                  <label className="block text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-1">Message</label>
                  <textarea
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    rows={6}
                    placeholder="Ex: Amanhã haverá reunião online às 9h. Não esqueçam de tirar fotos frontais nas ordens externas."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#2F5D8A]/30 focus:border-[#2F5D8A]"
                    maxLength={2000}
                    data-testid="announcement-message-input"
                  />
                  <p className="text-[10px] text-gray-400 mt-1">Editing the text will require every inspector to re-confirm.</p>
                </div>
                {error && <p className="text-xs text-red-600" data-testid="announcement-editor-error">{error}</p>}
              </div>
              <div className="flex items-center justify-end gap-2 px-5 py-3 bg-gray-50 border-t border-gray-200 rounded-b-lg">
                <button onClick={close} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">Cancel</button>
                <button
                  onClick={save}
                  disabled={saving}
                  className="flex items-center gap-2 px-4 py-2 bg-[#D1A054] hover:bg-[#B98F42] text-white text-sm font-semibold rounded-lg disabled:opacity-50"
                  data-testid="announcement-save-btn"
                >
                  <Check className="h-4 w-4" /> {saving ? "Saving…" : "Save"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default AdminAnnouncementsPage;
